import { useState, useRef, useCallback } from 'react';

import {
  createLivenessSession,
  getLivenessResult,
  getLivenessCredentials,
  KYCApiError,
} from '../services/kycApi';
import type { AwsLivenessCredentials, LivenessResult, LivenessSessionStatus } from '../types/kyc';
import { LIVENESS_SESSION_STATUS } from '../types/kyc';

// ─────────────────────────────────────────────────────────────────────────────
// Tipos del hook
// ─────────────────────────────────────────────────────────────────────────────

// Estados del ciclo de vida de la sesión de liveness en el frontend
const LIVENESS_HOOK_STATE = {
  IDLE: 'idle',           // Sin sesión activa
  CREATING: 'creating',   // Llamando al backend para crear la sesión
  READY: 'ready',         // session_id disponible — SDK de Amplify puede iniciar
  POLLING: 'polling',     // SDK terminó de transmitir — esperando veredicto del backend
  SUCCEEDED: 'succeeded', // Resultado disponible (is_live puede ser true o false)
  ERROR: 'error',         // Error técnico (red, servicio no disponible, sesión expirada)
} as const;

type LivenessHookState = (typeof LIVENESS_HOOK_STATE)[keyof typeof LIVENESS_HOOK_STATE];

interface UseLivenessSessionOptions {
  // Identificador del usuario KYC — opcional, se incluye en la evidencia almacenada en GCS
  userId?: string;
  // Nombre del archivo del documento de identidad en GCS (blob path, sin prefijo gs://).
  // Cuando se provee, activa la comparación facial en el liveness-service:
  // Rekognition CompareFaces verifica que la persona en la sesión coincida con el documento.
  docFilename?: string;
  // Número máximo de intentos de polling antes de declarar error (default: 10)
  maxPollAttempts?: number;
  // Intervalo entre intentos de polling en milisegundos (default: 2000)
  pollIntervalMs?: number;
}

interface UseLivenessSessionReturn {
  // Estado actual del ciclo de vida (para controlar qué renderizar)
  livenessState: LivenessHookState;
  // session_id retornado por el liveness-service — pasar al FaceLivenessDetector.sessionId
  livenessSessionId: string | null;
  // Resultado final — disponible cuando livenessState === SUCCEEDED
  result: LivenessResult | null;
  // Mensaje de error legible — disponible cuando livenessState === ERROR
  errorMessage: string | null;
  // Proveedor de credenciales AWS para FaceLivenessDetector.credentialProvider
  // Llama al liveness-service para obtener credenciales temporales de STS
  credentialProvider: () => Promise<AwsLivenessCredentials>;
  // Inicia el flujo: llama al backend y obtiene el session_id
  createSession: () => Promise<void>;
  // Llamar desde FaceLivenessDetector.onAnalysisComplete cuando el SDK termine de transmitir
  // Inicia el polling del resultado contra el liveness-service
  handleAnalysisComplete: (deviceInfo?: unknown) => Promise<void>;
  // Resetea el estado al inicial para permitir un reintento
  reset: () => void;
}

// ─────────────────────────────────────────────────────────────────────────────
// Constantes de polling
// ─────────────────────────────────────────────────────────────────────────────

const DEFAULT_MAX_POLL_ATTEMPTS = 10;
const DEFAULT_POLL_INTERVAL_MS = 2000;

// ─────────────────────────────────────────────────────────────────────────────
// Helpers internos
// ─────────────────────────────────────────────────────────────────────────────

// Traduce el status de la respuesta HTTP al tipo LivenessSessionStatus
function mapStatus(raw: string): LivenessSessionStatus {
  const valid = Object.values(LIVENESS_SESSION_STATUS) as string[];
  if (valid.includes(raw)) {
    return raw as LivenessSessionStatus;
  }
  return LIVENESS_SESSION_STATUS.FAILED;
}

// Espera un número de milisegundos (compatible con hooks React)
function wait(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

// ─────────────────────────────────────────────────────────────────────────────
// Hook principal
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Gestiona el ciclo completo de la prueba de vida con AWS Rekognition Face Liveness.
 *
 * Flujo:
 *   1. createSession()          → llama POST /liveness/sessions → obtiene session_id
 *   2. [SDK Amplify]            → transmite video directamente a Kinesis Video Streams
 *   3. handleAnalysisComplete() → SDK notifica que terminó → inicia polling
 *   4. [Polling]                → llama GET /liveness/sessions/{id}/result hasta SUCCEEDED/FAILED
 *   5. result disponible        → livenessState = SUCCEEDED, is_live y confidence accesibles
 *
 * La integración con Kinesis Video Streams (KVS) es transparente: el SDK de Amplify
 * FaceLivenessDetector maneja la conexión y transmisión a KVS internamente.
 * El backend solo crea la sesión y consulta el veredicto.
 * Ref: ADR-002-session-architecture.md del liveness-service.
 */
export function useLivenessSession(
  options: UseLivenessSessionOptions = {}
): UseLivenessSessionReturn {
  const {
    userId,
    docFilename,
    maxPollAttempts = DEFAULT_MAX_POLL_ATTEMPTS,
    pollIntervalMs = DEFAULT_POLL_INTERVAL_MS,
  } = options;

  const [livenessState, setLivenessState] = useState<LivenessHookState>(LIVENESS_HOOK_STATE.IDLE);
  const [livenessSessionId, setLivenessSessionId] = useState<string | null>(null);
  const [result, setResult] = useState<LivenessResult | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Referencia al contador de polling — permite cancelar desde handleAnalysisComplete
  const pollAttemptsRef = useRef(0);
  // Señal de cancelación para abortar el polling si el componente se desmonta
  const cancelledRef = useRef(false);

  // Inicia el flujo: llama al backend para crear la sesión y obtener el session_id
  const createSession = useCallback(async (): Promise<void> => {
    setLivenessState(LIVENESS_HOOK_STATE.CREATING);
    setErrorMessage(null);
    cancelledRef.current = false;

    try {
      const response = await createLivenessSession();
      setLivenessSessionId(response.session_id);
      setLivenessState(LIVENESS_HOOK_STATE.READY);
    } catch (err) {
      const message =
        err instanceof KYCApiError
          ? err.message
          : 'No se pudo iniciar la prueba de vida. Intenta de nuevo.';

      setErrorMessage(message);
      setLivenessState(LIVENESS_HOOK_STATE.ERROR);
    }
  }, []);

  // Polling del resultado — se ejecuta dentro de handleAnalysisComplete
  const pollForResult = useCallback(
    async (sessionId: string): Promise<void> => {
      pollAttemptsRef.current = 0;

      while (pollAttemptsRef.current < maxPollAttempts) {
        // Detener si el componente fue desmontado o el usuario resetó el flujo
        if (cancelledRef.current) {
          return;
        }

        try {
          const response = await getLivenessResult(sessionId, userId, docFilename);

          const status = mapStatus(response.status);

          if (status === LIVENESS_SESSION_STATUS.SUCCEEDED || status === LIVENESS_SESSION_STATUS.FAILED) {
            // Resultado definitivo disponible
            setResult({
              sessionId: response.session_id,
              status,
              confidence: response.confidence,
              isLive: response.is_live,
              faceMatch: response.face_match ?? null,
              faceMatchScore: response.face_match_score ?? null,
            });
            setLivenessState(LIVENESS_HOOK_STATE.SUCCEEDED);
            return;
          }

          if (status === LIVENESS_SESSION_STATUS.EXPIRED) {
            setErrorMessage('La sesión de prueba de vida expiró. Inicia el proceso de nuevo.');
            setLivenessState(LIVENESS_HOOK_STATE.ERROR);
            return;
          }

          // CREATED / IN_PROGRESS (HTTP 202) — video en procesamiento, reintentar
          pollAttemptsRef.current += 1;
          await wait(pollIntervalMs);

        } catch (err) {
          const message =
            err instanceof KYCApiError
              ? err.message
              : 'Error al obtener el resultado de la prueba de vida.';

          setErrorMessage(message);
          setLivenessState(LIVENESS_HOOK_STATE.ERROR);
          return;
        }
      }

      // Se agotaron los intentos sin resultado definitivo
      setErrorMessage('El procesamiento tardó demasiado. Intenta la prueba de vida de nuevo.');
      setLivenessState(LIVENESS_HOOK_STATE.ERROR);
    },
    [userId, docFilename, maxPollAttempts, pollIntervalMs]
  );

  // Llamar desde FaceLivenessDetector.onAnalysisComplete
  // Indica que el SDK terminó de transmitir el video a KVS e inicia el polling
  const handleAnalysisComplete = useCallback(async (_deviceInfo?: unknown): Promise<void> => {
    if (!livenessSessionId) {
      setErrorMessage('Error interno: sesión de liveness no inicializada.');
      setLivenessState(LIVENESS_HOOK_STATE.ERROR);
      return;
    }

    setLivenessState(LIVENESS_HOOK_STATE.POLLING);
    // pollForResult es async — se inicia el polling al completar el análisis del SDK
    await pollForResult(livenessSessionId);
  }, [livenessSessionId, pollForResult]);

  // Proveedor de credenciales AWS para FaceLivenessDetector.credentialProvider
  // Llama al liveness-service para obtener credenciales temporales de STS que permiten
  // al SDK de Amplify conectarse directamente a Rekognition / KVS desde el navegador
  const credentialProvider = useCallback(async (): Promise<AwsLivenessCredentials> => {
    const creds = await getLivenessCredentials();
    return {
      accessKeyId: creds.access_key_id,
      secretAccessKey: creds.secret_access_key,
      sessionToken: creds.session_token,
      expiration: creds.expiration ? new Date(creds.expiration) : undefined,
    };
  }, []);

  // Resetea el estado al inicial para permitir un nuevo intento
  const reset = useCallback((): void => {
    cancelledRef.current = true;
    pollAttemptsRef.current = 0;
    setLivenessState(LIVENESS_HOOK_STATE.IDLE);
    setLivenessSessionId(null);
    setResult(null);
    setErrorMessage(null);
  }, []);

  return {
    livenessState,
    livenessSessionId,
    result,
    errorMessage,
    credentialProvider,
    createSession,
    handleAnalysisComplete,
    reset,
  };
}

// Re-exportar tipos y constantes para uso en componentes
export { LIVENESS_HOOK_STATE };
export type { LivenessHookState, UseLivenessSessionOptions, UseLivenessSessionReturn };
